import { useState, useEffect } from 'react';
import { useQuery, useLazyQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Box, Grid, Button, Accordion, Typography, AccordionSummary } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useSelector } from 'react-redux';

import { selectUser } from '../../features/auth/authSlice';
import { IProjectCardDetails } from '../../types/project';
import ProjectCard from './ProjectCard';
import { useAppDispatch } from '../../app/hooks';
import { reset } from '../../features/project/projectSlice';
import storeyviewLogo from '../../assets/logos/sv_logo_green.png';
import { theme } from '../../theme';

function sortProjects(projects: any[]) {
  return [...projects].sort(
    (a: any, b: any) =>
      b.acceptedCount - a.acceptedCount ||
      b.pendingCount - a.pendingCount ||
      b.taskCount - a.taskCount ||
      new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf()
  );
}

function Portal(props: any) {
  const storeDispatch = useAppDispatch();
  const [projects, setProjects] = useState<any[]>([]);
  const [nonActiveProjects, setNonActiveProjects] = useState<any[]>([]);
  const [nonActiveLoaded, setNonActiveLoaded] = useState(false);
  const user = useSelector(selectUser);

  useEffect(() => {
    storeDispatch(reset());
  }, [storeDispatch]);

  const { loading } = useQuery(PROJECTS, {
    skip: !user,
    fetchPolicy: 'cache-first',
    variables: { active: user?.type !== 'Developer' ? true : undefined },
    onCompleted: (data) => {
      setProjects(sortProjects(data.getWorksheetCount));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [loadNonActiveProjects, { loading: loadingNonActive }] = useLazyQuery(PROJECTS, {
    fetchPolicy: 'cache-first',
    variables: { active: false },
    onCompleted: (data) => {
      setNonActiveProjects(sortProjects(data.getWorksheetCount));
      setNonActiveLoaded(true);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const handleNonActiveExpand = (_event: React.SyntheticEvent, expanded: boolean) => {
    if (expanded && !nonActiveLoaded) {
      loadNonActiveProjects();
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f4f4f2', minHeight: '100vh' }}>
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '1140px',
          margin: '0 auto',
          padding: '20px 24px',
          borderBottom: `3px solid ${theme.palette.secondary.main}`,
        }}
      >
        <img src={storeyviewLogo} alt="storeyview" style={{ height: '26px' }} />
        {user?.type === 'Manager' ? (
          <Link
            style={{
              textDecoration: 'none',
            }}
            to="/create-project"
          >
            <Button
              variant={'contained'}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.main },
              }}
            >
              Create Project
            </Button>
          </Link>
        ) : null}
      </Box>
      <Box
        py={5}
        sx={{
          flexGrow: 1,
          maxWidth: '1140px',
          margin: '0 auto',
          padding: '0px',
        }}
      >
        {!loading ? (
          <>
            <Grid
              container
              spacing={3}
              sx={{
                margin: 0,
                width: '100%',
              }}
            >
              {projects.length > 0
                ? projects.map((project: IProjectCardDetails, index: number) => {
                    return <ProjectCard project={project} key={index} />;
                  })
                : null}
            </Grid>
            {user.type !== 'Developer' ? (
              <Accordion
                onChange={handleNonActiveExpand}
                sx={{
                  mt: 3,
                  backgroundColor: '#f4f4f2',
                  boxShadow: 'none',
                  borderRadius: '6px !important',
                  '&::before': {
                    display: 'none',
                  },
                }}
              >
                <AccordionSummary
                  sx={{ pl: 3 }}
                  expandIcon={<ArrowDownwardIcon style={{ color: theme.palette.primary.main, border: 'none' }} />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    Non Active Projects
                  </Typography>
                </AccordionSummary>
                <Grid
                  container
                  spacing={3}
                  sx={{
                    margin: 0,
                    width: '100%',
                    padding: '0 0 16px',
                  }}
                >
                  {loadingNonActive ? (
                    <Typography sx={{ pl: 3, color: theme.palette.text.secondary }}>Loading...</Typography>
                  ) : nonActiveProjects.length > 0 ? (
                    nonActiveProjects.map((project: IProjectCardDetails, index: number) => {
                      return <ProjectCard project={project} key={index} />;
                    })
                  ) : null}
                </Grid>
              </Accordion>
            ) : null}
          </>
        ) : null}
      </Box>
    </Box>
  );
}

const PROJECTS = gql`
  query getWorksheetCount($active: Boolean) {
    getWorksheetCount(active: $active) {
      _id
      name
      acceptedCount
      pendingCount
      taskCount
      createdAt
      active
    }
  }
`;

export default Portal;
